import batchStructureService from 'ApiService/Admin/batchService';
import courseService from 'ApiService/Admin/courseService';
import studentService from 'ApiService/Admin/studentService';
import commonServices from 'ApiService/Common/commonServices';
import InputField from 'components/fields/InputField';
import TextField from 'components/fields/TextField';
import { AuthContext } from 'contexts/AuthContext';
import ConfirmationModal from 'pages/Common/ConfirmationModal';
import Modal from 'pages/Student/Assignment/UploadAssignment/Steps/Model';
import StudentOverviewTable from 'pages/Student/Overview/components/StudentOverviewTable';
import { useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { RiEyeCloseLine, RiEyeLine } from 'react-icons/ri';
import CourseEnrollmentDisplay from './EnrollmentCard';
import FeeDetails from './FeeDetails';
import StudentProfileCard from './StudentProfileCard';

const Index = ({ selectedStudent, handleBackClick, selectedBranch }) => {
  const BASE_URL = `${process.env.REACT_APP_API_BASE_URL.replace(
    '/api/v1',
    ''
  )}`;
  const [getFiles, setGetFiles] = useState([]);
  const [updatedStudent, setUpdatedStudent] = useState({
    ...selectedStudent,
  });
  const [isEditing, setIsEditing] = useState(false);
  const { state } = useContext(AuthContext);
  const [password, setPassword] = useState('');
  const [showFirstConfirmation, setShowFirstConfirmation] = useState(false);
  const [showEditConfirmation, setShowEditConfirmation] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedStandard, setSelectedStandard] = useState(null);
  const [mcqData, setMcqData] = useState(null);
  const [qaData, setQaData] = useState(null);
  const [mcqAssignmentData, setAssignmentMcqData] = useState(null);
  const [qaAssignmentData, setQaAssignmentData] = useState(null);
  const [loadingStudentData, setLoadingStudentData] = useState(false);
  const [mockData, setMockData] = useState(null);
  const [finalMockData, setFinalMockData] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [feesData, setFeesData] = useState({
    totalAmount: '',
    isFullPayment: true,
    installments: [],
  });

  /* ============ ENROLLMENT (course / batch) CORRECTION ============ */
  // Populated enrollments used for display. Kept in local state so a correction
  // refreshes both the edit block and the Course Enrollments card in place.
  const [enrollments, setEnrollments] = useState(
    selectedStudent.enrollments || []
  );
  const currentEnrollment = enrollments.find((e) => e.current) || null;
  const currentEnrollmentIndex = currentEnrollment
    ? (updatedStudent.enrollments || []).findIndex(
        (e) => String(e._id) === String(currentEnrollment._id)
      )
    : -1;

  const handleCurrentEnrollmentFieldChange = (field, value) => {
    if (currentEnrollmentIndex < 0) return;
    setUpdatedStudent((prev) => {
      const next = [...(prev.enrollments || [])];
      next[currentEnrollmentIndex] = {
        ...next[currentEnrollmentIndex],
        [field]: value,
      };
      return { ...prev, enrollments: next };
    });
  };

  const [showEnrollmentEditor, setShowEnrollmentEditor] = useState(false);
  const [courses, setCourses] = useState([]);
  const [batchStructures, setBatchStructures] = useState([]);
  const [availableBatches, setAvailableBatches] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [loadingStructures, setLoadingStructures] = useState(false);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [correctionSaving, setCorrectionSaving] = useState(false);
  const [showCorrectionConfirmation, setShowCorrectionConfirmation] =
    useState(false);
  const [correction, setCorrection] = useState({
    courseId: '',
    batchStructureId: '',
    batchId: '',
    reason: '',
  });

  const fetchCoursesForCorrection = async () => {
    setLoadingCourses(true);
    try {
      const response = await courseService.getAllCourses(selectedBranch);
      if (response.data.success) {
        setCourses(response.data.courses || response.data.data || []);
      } else {
        toast.error('Failed to fetch courses.');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to fetch courses.');
    } finally {
      setLoadingCourses(false);
    }
  };

  const fetchStructuresForCorrection = async (courseId) => {
    if (!courseId) {
      setBatchStructures([]);
      return;
    }
    setLoadingStructures(true);
    try {
      const response = await batchStructureService.getBatchStructuresByCourse(
        courseId
      );
      if (response.data.success) {
        setBatchStructures(response.data.batchStructures || []);
      } else {
        toast.error('Failed to fetch batch structures.');
        setBatchStructures([]);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to fetch batch structures.'
      );
      setBatchStructures([]);
    } finally {
      setLoadingStructures(false);
    }
  };

  const fetchBatchesForCorrection = async (structureId) => {
    if (!structureId) {
      setAvailableBatches([]);
      return;
    }
    setLoadingBatches(true);
    try {
      const response = await batchStructureService.getBatchesByBatchStructure(
        structureId,
        selectedBranch
      );
      if (response.data.success) {
        setAvailableBatches(response.data.batches || []);
      } else {
        toast.error('Failed to fetch batches.');
        setAvailableBatches([]);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to fetch batches.');
      setAvailableBatches([]);
    } finally {
      setLoadingBatches(false);
    }
  };

  const openEnrollmentEditor = async () => {
    if (!currentEnrollment) return;
    const courseId = currentEnrollment.course?._id || currentEnrollment.course || '';
    const structureId =
      currentEnrollment.batchStructure?._id ||
      currentEnrollment.batchStructure ||
      '';
    setCorrection({
      courseId,
      batchStructureId: structureId,
      batchId: currentEnrollment.batch?._id || currentEnrollment.batch || '',
      reason: '',
    });
    setShowEnrollmentEditor(true);
    if (!courses.length) await fetchCoursesForCorrection();
    if (courseId) await fetchStructuresForCorrection(courseId);
    if (structureId) await fetchBatchesForCorrection(structureId);
  };

  const closeEnrollmentEditor = () => {
    setShowEnrollmentEditor(false);
    setBatchStructures([]);
    setAvailableBatches([]);
  };

  const handleCorrectionCourseChange = async (courseId) => {
    setCorrection((prev) => ({
      ...prev,
      courseId,
      batchStructureId: '',
      batchId: '',
    }));
    setAvailableBatches([]);
    await fetchStructuresForCorrection(courseId);
  };

  const handleCorrectionStructureChange = async (structureId) => {
    setCorrection((prev) => ({
      ...prev,
      batchStructureId: structureId,
      batchId: '',
    }));
    await fetchBatchesForCorrection(structureId);
  };

  const handleCorrectEnrollment = async () => {
    setShowCorrectionConfirmation(false);
    setCorrectionSaving(true);
    try {
      const response = await studentService.correctEnrollment({
        studentId: selectedStudent._id,
        enrollmentId: currentEnrollment._id,
        courseId: correction.courseId,
        batchStructureId: correction.batchStructureId,
        batchId: correction.batchId,
        ...(correction.reason && { reason: correction.reason }),
        ...(selectedBranch && { selectedBranch }),
      });

      if (response.data.success) {
        const nextEnrollments = response.data.data?.enrollments || [];
        setEnrollments(nextEnrollments);
        setUpdatedStudent((prev) => ({ ...prev, enrollments: nextEnrollments }));
        toast.success(response.data.message || 'Enrollment corrected.');
        closeEnrollmentEditor();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to correct enrollment.'
      );
    } finally {
      setCorrectionSaving(false);
    }
  };

  const correctionChanged =
    !!currentEnrollment &&
    (String(correction.courseId) !==
      String(currentEnrollment.course?._id || currentEnrollment.course) ||
      String(correction.batchId) !==
        String(currentEnrollment.batch?._id || currentEnrollment.batch) ||
      String(correction.batchStructureId) !==
        String(
          currentEnrollment.batchStructure?._id ||
            currentEnrollment.batchStructure
        ));

  const selectedCorrectionCourseName =
    courses.find((c) => c._id === correction.courseId)?.courseName || '';
  const selectedCorrectionBatchNumber =
    availableBatches.find((b) => b._id === correction.batchId)?.batchNumber ||
    '';

  const handleFeeFieldChange = (field, value) => {
    setFeesData((prev) => ({ ...prev, [field]: value }));
  };

  const handleInstallmentChange = (index, field, value) => {
    setFeesData((prev) => {
      const updated = [...prev.installments];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, installments: updated };
    });
  };

  const addInstallment = () => {
    setFeesData((prev) => ({
      ...prev,
      installments: [
        ...prev.installments,
        { amount: '', dueDate: '', isEmiInstallment: false, afterLoanDeduction: '' },
      ],
    }));
  };

  const removeInstallment = (index) => {
    setFeesData((prev) => ({
      ...prev,
      installments: prev.installments.filter((_, i) => i !== index),
    }));
  };

  const handleFileClick = (file) => {
    setSelectedFile(file);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedFile(null);
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
    setTimeout(() => setShowPassword(false), 2000);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'password') {
      setPassword(value);
    } else {
      setUpdatedStudent((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSaveButtonClick = () => {
    setShowEditConfirmation(true);
  };

  const handleEditStudent = async () => {
    try {
      setShowEditConfirmation(false);
      const payload = {
        studentId: selectedStudent._id,
        name: updatedStudent.name,
        parentName: updatedStudent.parentName,
        ParentContactNo: updatedStudent.ParentContactNo,
        email: updatedStudent.email,
        contactNumber: updatedStudent.contactNumber,
        gender: updatedStudent.gender,
        dateOfBirth: updatedStudent.dateOfBirth,
        password,
        address: updatedStudent.address,
        jobLocationPreference: updatedStudent.jobLocationPreference,
        preferredSalary: updatedStudent.preferredSalary,
        termsAndConditionType: updatedStudent.termsAndConditionType,
        // Only sent when loaded/typed, so a doc fetched without it can't wipe the note
        ...(updatedStudent.staffNotes !== undefined && {
          staffNotes: updatedStudent.staffNotes,
        }),
        academicRecords: updatedStudent.academicRecords,
        workExperience: updatedStudent.workExperience,
        enrollments: updatedStudent.enrollments,
        fees: {
          totalAmount: feesData.totalAmount ? Number(feesData.totalAmount) : undefined,
          isFullPayment: feesData.isFullPayment,
          installments: feesData.installments.map((i) => ({
            ...(i._id && { _id: i._id }),
            amount: i.amount ? Number(i.amount) : undefined,
            dueDate: i.dueDate || undefined,
            isPaid: i.isPaid || false,
            isEmiInstallment: i.isEmiInstallment || false,
            ...(i.afterLoanDeduction && { afterLoanDeduction: Number(i.afterLoanDeduction) }),
          })),
        },
      };
      console.log(payload, 'payload');
      const response = await studentService.updateStudent(payload);

      if (response.data.success) {
        toast.success('Student updated successfully!');
        setIsEditing(false);
        handleBackClick();
        // getStudentInfo();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response.data.message);
    }
  };

  const handleDeactivateStudent = async () => {
    try {
      const response = await commonServices.blockUser({
        userId: selectedStudent._id,
        role: 'Student',
      });

      if (response.data.success) {
        toast.success(response.data.message);
        setShowFirstConfirmation(false);
        handleBackClick();
      } else {
        toast.error(response.data.message);
      }
      setShowFirstConfirmation(false);
    } catch (error) {
      console.error(error);
      toast.error(error.response.data.message);
    }
  };

  const fetchData = async () => {
    try {
      const response = await studentService.getstudentUploadedProfile(
        selectedStudent._id
      );
      if (response.data.success) {
        setGetFiles(response.data.data || []);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to fetch attended tests.'
      );
    }
  };

  useEffect(() => {
    if (selectedStudent.dateOfBirth) {
      setUpdatedStudent((prev) => ({
        ...prev,
        dateOfBirth: new Date(selectedStudent.dateOfBirth)
          .toISOString()
          .split('T')[0],
      }));
    }
  }, [selectedStudent.dateOfBirth]);

  useEffect(() => {
    fetchData();
  }, [state.user, selectedStudent._id]);

  const fetchStudentOverviewData = async () => {
    setLoadingStudentData(true);
    try {
      const practicalResponse = await studentService.getStudentOverviewData(
        selectedStudent._id
      );
      if (practicalResponse.data.success) {
        setSelectedStandard(practicalResponse.data.Data);
      }

      const mcqResponse = await studentService.getMcqTestOverviewData(
        selectedStudent._id
      );
      if (mcqResponse.data.success) {
        setMcqData(mcqResponse.data.data);
      }

      const qaResponse = await studentService.getQaTestOverviewData(
        selectedStudent._id
      );
      if (qaResponse.data.success) {
        setQaData(qaResponse.data.data);
      }

      const mcqAssignmentResponse =
        await studentService.getMCQAssignmentOverviewData(selectedStudent._id);
      if (mcqAssignmentResponse.data.success) {
        setAssignmentMcqData(mcqAssignmentResponse.data.data);
      }

      const qaAssignmentResponse =
        await studentService.getQAAssignmentOverviewData(selectedStudent._id);
      if (qaAssignmentResponse.data.success) {
        setQaAssignmentData(qaAssignmentResponse.data.data);
      }

      const mockResponse = await studentService.getStudentExternalMockOverview(selectedStudent._id);
      if (mockResponse.data.success) {
        setFinalMockData(mockResponse.data.data);
      }

      const practiceResponse = await studentService.getStudentExternalPracticeOverview(selectedStudent._id);
      if (practiceResponse.data.success) {
        setMockData(practiceResponse.data.data);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to fetch data');
    } finally {
      setLoadingStudentData(false);
    }
  };

  useEffect(() => {
    fetchStudentOverviewData();
  }, []);

  return (
    <div className="flex h-full w-full flex-col items-center justify-center p-2">
      <div className=" mx-auto w-full max-w-6xl text-navy-700 dark:text-white">
        <div className="flex flex-col gap-6 md:flex-row">
          <StudentProfileCard
            selectedStudent={selectedStudent}
            BASE_URL={BASE_URL}
            handleFileClick={handleFileClick}
            setShowFirstConfirmation={setShowFirstConfirmation}
            setIsEditing={setIsEditing}
          />
          <div className="w-full md:w-2/3">
            {isEditing ? (
              <div className="rounded-lg border border-dashed border-brand-500 bg-white p-6 shadow-md dark:bg-navy-900">
                <h3 className="mb-4 text-lg font-semibold">
                  Update Student Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {/* Basic Information */}
                  <div>
                    <InputField
                      label="Name"
                      type="text"
                      name="name"
                      value={updatedStudent.name}
                      onChange={handleInputChange}
                      className="w-full rounded border border-gray-300 px-3 py-2 dark:bg-navy-900"
                    />
                  </div>
                  <div>
                    <InputField
                      label="Parent Name"
                      type="text"
                      name="parentName"
                      value={updatedStudent.parentName}
                      onChange={handleInputChange}
                      className="w-full rounded border border-gray-300 px-3 py-2 dark:bg-navy-900"
                    />
                  </div>
                  <div>
                    <InputField
                      label="Contact Number"
                      type="text"
                      name="contactNumber"
                      value={updatedStudent.contactNumber}
                      onChange={handleInputChange}
                      className="w-full rounded border border-gray-300 px-3 py-2 dark:bg-navy-900"
                    />
                  </div>
                  <div>
                    <InputField
                      label="Parent Contact Number"
                      type="text"
                      name="ParentContactNo"
                      value={updatedStudent.ParentContactNo}
                      onChange={handleInputChange}
                      className="w-full rounded border border-gray-300 px-3 py-2 dark:bg-navy-900"
                    />
                  </div>

                  {/* Contact Details */}
                  <div>
                    <InputField
                      label="Email"
                      type="email"
                      name="email"
                      value={updatedStudent.email}
                      onChange={handleInputChange}
                      className="w-full rounded border border-gray-300 px-3 py-2 dark:bg-navy-900"
                    />
                  </div>
                  <div>
                    <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">
                      Gender:
                    </label>
                    <select
                      name="gender"
                      value={updatedStudent.gender}
                      onChange={handleInputChange}
                      className="mt-2 w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
                    >
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                      <option value="Prefer not to say">
                        Prefer not to say
                      </option>
                    </select>
                  </div>

                  {/* Address Details */}
                  <div>
                    <InputField
                      label="Street"
                      type="text"
                      name="street"
                      value={updatedStudent.address?.street || ''}
                      onChange={(e) => {
                        const updatedAddress = {
                          ...updatedStudent.address,
                          street: e.target.value,
                        };
                        setUpdatedStudent((prev) => ({
                          ...prev,
                          address: updatedAddress,
                        }));
                      }}
                      className="w-full rounded border border-gray-300 px-3 py-2 dark:bg-navy-900"
                    />
                  </div>
                  <div>
                    <InputField
                      label="City"
                      type="text"
                      name="city"
                      value={updatedStudent.address?.city || ''}
                      onChange={(e) => {
                        const updatedAddress = {
                          ...updatedStudent.address,
                          city: e.target.value,
                        };
                        setUpdatedStudent((prev) => ({
                          ...prev,
                          address: updatedAddress,
                        }));
                      }}
                      className="w-full rounded border border-gray-300 px-3 py-2 dark:bg-navy-900"
                    />
                  </div>
                  <div>
                    <InputField
                      label="State"
                      type="text"
                      name="state"
                      value={updatedStudent.address?.state || ''}
                      onChange={(e) => {
                        const updatedAddress = {
                          ...updatedStudent.address,
                          state: e.target.value,
                        };
                        setUpdatedStudent((prev) => ({
                          ...prev,
                          address: updatedAddress,
                        }));
                      }}
                      className="w-full rounded border border-gray-300 px-3 py-2 dark:bg-navy-900"
                    />
                  </div>
                  <div>
                    <InputField
                      label="Postal Code"
                      type="text"
                      name="postalCode"
                      value={updatedStudent.address?.postalCode || ''}
                      onChange={(e) => {
                        const updatedAddress = {
                          ...updatedStudent.address,
                          postalCode: e.target.value,
                        };
                        setUpdatedStudent((prev) => ({
                          ...prev,
                          address: updatedAddress,
                        }));
                      }}
                      className="w-full rounded border border-gray-300 px-3 py-2 dark:bg-navy-900"
                    />
                  </div>

                  <div>
                    <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">
                      Country
                    </label>
                    <select
                      name="country"
                      value={updatedStudent.address?.country || ''}
                      onChange={(e) => {
                        const updatedAddress = {
                          ...updatedStudent.address,
                          country: e.target.value,
                        };
                        setUpdatedStudent((prev) => ({
                          ...prev,
                          address: updatedAddress,
                        }));
                      }}
                      className="mt-2 w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
                    >
                      <option value="">Select Country</option>
                      <option value="India">India</option>
                      <option value="Afghanistan">Afghanistan</option>
                      <option value="Bangladesh">Bangladesh</option>
                      <option value="Bhutan">Bhutan</option>
                      <option value="Nepal">Nepal</option>
                      <option value="Sri Lanka">Sri Lanka</option>
                      <option value="Pakistan">Pakistan</option>
                      <option value="Maldives">Maldives</option>
                      <option value="Myanmar">Myanmar</option>
                      <option value="United Arab Emirates">United Arab Emirates</option>
                      <option value="Saudi Arabia">Saudi Arabia</option>
                      <option value="Oman">Oman</option>
                      <option value="Qatar">Qatar</option>
                      <option value="Kuwait">Kuwait</option>
                      <option value="Bahrain">Bahrain</option>
                    </select>
                  </div>

                  {/* Additional Details */}
                  <div>
                    <InputField
                      label="Date of Birth"
                      name="dateOfBirth"
                      type="date"
                      value={
                        updatedStudent?.dateOfBirth
                          ? new Date(updatedStudent.dateOfBirth)
                              .toISOString()
                              .split('T')[0]
                          : ''
                      }
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 dark:bg-navy-800"
                    />
                  </div>

                  {/* Job Location Preferences */}
                  <div>
                    <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">
                      Job Location Preferences
                    </label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {[
                        'Mumbai',
                        'Delhi',
                        'Bangalore',
                        'Hyderabad',
                        'Chennai',
                        'Navi Mumbai',
                        'Pune',
                        'Ahmedabad',
                        'Surat',
                        'Lucknow',
                        'Noida',
                      ].map((location) => (
                        <label
                          key={location}
                          className="inline-flex items-center"
                        >
                          <input
                            type="checkbox"
                            name="jobLocationPreference"
                            value={location}
                            checked={(
                              updatedStudent.jobLocationPreference || []
                            ).includes(location)}
                            onChange={(e) => {
                              const locations =
                                updatedStudent.jobLocationPreference || [];
                              const newLocations = e.target.checked
                                ? [...locations, location]
                                : locations.filter((loc) => loc !== location);

                              setUpdatedStudent((prev) => ({
                                ...prev,
                                jobLocationPreference: newLocations,
                              }));
                            }}
                            className="form-checkbox"
                          />
                          <span className="ml-2">{location}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Placement Priority */}
                  <div>
                    <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">
                      Placement Priority
                    </label>
                    <select
                      name="placementPriority"
                      value={updatedStudent.placementPriority}
                      onChange={handleInputChange}
                      className="mt-2 w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
                    >
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                    </select>
                  </div>

                  {/* Preferred Salary */}
                  <div>
                    <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">
                      Preferred Salary
                    </label>
                    <select
                      name="preferredSalary"
                      value={updatedStudent.preferredSalary || 'Any Salary'}
                      onChange={handleInputChange}
                      className="mt-2 w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
                    >
                      <option value="Any Salary">Any Salary</option>
                      <option value="Below 3 LPA">Below 3 LPA</option>
                      <option value="Below 4 LPA">Below 4 LPA</option>
                      <option value="Below 5 LPA">Below 5 LPA</option>
                      <option value="More than 5 LPA">More than 5 LPA</option>
                    </select>
                  </div>

                  {/* Terms and Condition Type */}
                  <div>
                    <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">
                      Terms and Condition Format
                    </label>
                    <select
                      name="termsAndConditionType"
                      value={updatedStudent.termsAndConditionType || 'Job Guarantee Training'}
                      onChange={handleInputChange}
                      className="mt-2 w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
                    >
                      <option value="Job Guarantee Training">Job Guarantee Training</option>
                      <option value="Training only">Training only</option>
                      <option value="Bootcamp Training Program">Bootcamp Training Program</option>
                      <option value="Job Assistance Program">Job Assistance Program</option>
                      <option value="Interview Guarantee Program">Interview Guarantee Program</option>
                      <option value="100% Placement Training Program">100% Placement Training Program</option>
                    </select>
                  </div>

                  {/* Staff Notes (internal, not visible to student) */}
                  <div className="col-span-full">
                    <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">
                      Staff Notes (internal – not visible to student)
                    </label>
                    <textarea
                      name="staffNotes"
                      rows={3}
                      value={updatedStudent.staffNotes || ''}
                      onChange={handleInputChange}
                      placeholder="Notes for admin / staff only"
                      className="mt-2 w-full rounded-xl border border-gray-200 p-3 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className="text-md mb-4 font-semibold">
                    Academic Records
                  </h4>
                  {(updatedStudent.academicRecords || []).map(
                    (record, index) => (
                      <div
                        key={record._id || index}
                        className="relative mb-4 grid grid-cols-3 gap-4 rounded border p-4"
                      >
                        <div>
                          <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">
                            Level
                          </label>
                          <select
                            name={`academicRecords[${index}].level`}
                            value={record.level}
                            onChange={(e) => {
                              const updatedRecords = [
                                ...(updatedStudent.academicRecords || []),
                              ];
                              updatedRecords[index] = {
                                ...updatedRecords[index],
                                level: e.target.value,
                              };
                              setUpdatedStudent((prev) => ({
                                ...prev,
                                academicRecords: updatedRecords,
                              }));
                            }}
                            className="mt-2 w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-900 dark:text-white"
                          >
                            <option value="SSC">SSC</option>
                            <option value="HSC">HSC</option>
                            <option value="Diploma">Diploma</option>
                            <option value="Graduation">Graduation</option>
                            <option value="Post Graduation">
                              Post Graduation
                            </option>
                          </select>
                        </div>
                        <div>
                          <InputField
                            label=" Stream"
                            type="text"
                            value={record.stream || ''}
                            onChange={(e) => {
                              const updatedRecords = [
                                ...(updatedStudent.academicRecords || []),
                              ];
                              updatedRecords[index] = {
                                ...updatedRecords[index],
                                stream: e.target.value,
                              };
                              setUpdatedStudent((prev) => ({
                                ...prev,
                                academicRecords: updatedRecords,
                              }));
                            }}
                            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                            placeholder="Enter Stream"
                          />
                        </div>
                        <div>
                          <InputField
                            label="Institution"
                            type="text"
                            value={record.institutionName || ''}
                            onChange={(e) => {
                              const updatedRecords = [
                                ...(updatedStudent.academicRecords || []),
                              ];
                              updatedRecords[index] = {
                                ...updatedRecords[index],
                                institutionName: e.target.value,
                              };
                              setUpdatedStudent((prev) => ({
                                ...prev,
                                academicRecords: updatedRecords,
                              }));
                            }}
                            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                            placeholder="Enter Institution Name"
                          />
                        </div>
                        <div>
                          <InputField
                            type="text"
                            label="Board"
                            value={record.board || ''}
                            onChange={(e) => {
                              const updatedRecords = [
                                ...(updatedStudent.academicRecords || []),
                              ];
                              updatedRecords[index] = {
                                ...updatedRecords[index],
                                board: e.target.value,
                              };
                              setUpdatedStudent((prev) => ({
                                ...prev,
                                academicRecords: updatedRecords,
                              }));
                            }}
                            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                            placeholder="Enter Board"
                          />
                        </div>
                        <div>
                          <InputField
                            label="Year of Completion"
                            type="number"
                            value={record.yearOfCompletion || ''}
                            onChange={(e) => {
                              const updatedRecords = [
                                ...(updatedStudent.academicRecords || []),
                              ];
                              updatedRecords[index] = {
                                ...updatedRecords[index],
                                yearOfCompletion: e.target.value,
                              };
                              setUpdatedStudent((prev) => ({
                                ...prev,
                                academicRecords: updatedRecords,
                              }));
                            }}
                            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                            placeholder="Enter Year"
                          />
                        </div>
                        <div>
                          <InputField
                            label="Percentage"
                            type="number"
                            value={record.percentage || ''}
                            onChange={(e) => {
                              const updatedRecords = [
                                ...(updatedStudent.academicRecords || []),
                              ];
                              updatedRecords[index] = {
                                ...updatedRecords[index],
                                percentage: e.target.value,
                              };
                              setUpdatedStudent((prev) => ({
                                ...prev,
                                academicRecords: updatedRecords,
                              }));
                            }}
                            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                            placeholder="Enter Percentage"
                          />
                        </div>
                        <div>
                          <InputField
                            label="GPA"
                            type="number"
                            value={record.gpa || ''}
                            onChange={(e) => {
                              const updatedRecords = [
                                ...(updatedStudent.academicRecords || []),
                              ];
                              updatedRecords[index] = {
                                ...updatedRecords[index],
                                gpa:
                                  e.target.value === ''
                                    ? undefined
                                    : parseFloat(e.target.value) || undefined,
                              };
                              setUpdatedStudent((prev) => ({
                                ...prev,
                                academicRecords: updatedRecords,
                              }));
                            }}
                            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                            placeholder="Enter gpa"
                          />
                        </div>

                        {/* Remove Button - Only show for newly added records (those without an _id) */}
                        {!record._id && (
                          <button
                            onClick={() => {
                              const updatedRecords = [
                                ...(updatedStudent.academicRecords || []),
                              ];
                              updatedRecords.splice(index, 1);
                              setUpdatedStudent((prev) => ({
                                ...prev,
                                academicRecords: updatedRecords,
                              }));
                            }}
                            className="absolute right-2 top-2 rounded bg-red-500 px-2 py-1 text-xs text-white hover:bg-red-600"
                            type="button"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    )
                  )}
                  <button
                    onClick={() => {
                      const newRecord = {
                        level: 'SSC',
                        stream: '',
                        institutionName: '',
                        board: '',
                        yearOfCompletion: null,
                        percentage: '',
                        gpa: '',
                      };
                      setUpdatedStudent((prev) => ({
                        ...prev,
                        academicRecords: [
                          ...(prev.academicRecords || []),
                          newRecord,
                        ],
                      }));
                    }}
                    className="mt-2 rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
                    type="button"
                  >
                    Add Academic Record
                  </button>
                </div>

                {/* Work Experience Section */}
                <div className="mb-6">
                  <h4 className="text-md mb-4 font-semibold">
                    Work Experience
                  </h4>
                  {(updatedStudent.workExperience || []).map((exp, index) => (
                    <div
                      key={exp._id || index}
                      className="relative mb-4 grid grid-cols-3 gap-4 rounded border p-4"
                    >
                      <div>
                        <label className="block text-sm font-medium text-gray-700"></label>
                        <InputField
                          label="Company Name"
                          type="text"
                          value={exp.companyName || ''}
                          onChange={(e) => {
                            const updatedExperiences = [
                              ...(updatedStudent.workExperience || []),
                            ];
                            updatedExperiences[index] = {
                              ...updatedExperiences[index],
                              companyName: e.target.value,
                            };
                            setUpdatedStudent((prev) => ({
                              ...prev,
                              workExperience: updatedExperiences,
                            }));
                          }}
                          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                          placeholder="Enter Company Name"
                        />
                      </div>
                      <div>
                        <InputField
                          label="Position"
                          type="text"
                          value={exp.position || ''}
                          onChange={(e) => {
                            const updatedExperiences = [
                              ...(updatedStudent.workExperience || []),
                            ];
                            updatedExperiences[index] = {
                              ...updatedExperiences[index],
                              position: e.target.value,
                            };
                            setUpdatedStudent((prev) => ({
                              ...prev,
                              workExperience: updatedExperiences,
                            }));
                          }}
                          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                          placeholder="Enter Position"
                        />
                      </div>
                      <div>
                        <InputField
                          label="Start Date"
                          type="date"
                          value={
                            exp.startDate
                              ? new Date(exp.startDate)
                                  .toISOString()
                                  .split('T')[0]
                              : ''
                          }
                          onChange={(e) => {
                            const updatedExperiences = [
                              ...(updatedStudent.workExperience || []),
                            ];
                            updatedExperiences[index] = {
                              ...updatedExperiences[index],
                              startDate: new Date(e.target.value).toISOString(),
                            };
                            setUpdatedStudent((prev) => ({
                              ...prev,
                              workExperience: updatedExperiences,
                            }));
                          }}
                          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                        />
                      </div>
                      <div>
                        <InputField
                          label="End Date"
                          type="date"
                          value={
                            exp.endDate
                              ? new Date(exp.endDate)
                                  .toISOString()
                                  .split('T')[0]
                              : ''
                          }
                          onChange={(e) => {
                            const updatedExperiences = [
                              ...(updatedStudent.workExperience || []),
                            ];
                            updatedExperiences[index] = {
                              ...updatedExperiences[index],
                              endDate: new Date(e.target.value).toISOString(),
                            };
                            setUpdatedStudent((prev) => ({
                              ...prev,
                              workExperience: updatedExperiences,
                            }));
                          }}
                          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                        />
                      </div>
                      <div>
                        <TextField
                          label="Responsibilities"
                          value={exp.responsibilities || ''}
                          onChange={(e) => {
                            const updatedExperiences = [
                              ...(updatedStudent.workExperience || []),
                            ];
                            updatedExperiences[index] = {
                              ...updatedExperiences[index],
                              responsibilities: e.target.value,
                            };
                            setUpdatedStudent((prev) => ({
                              ...prev,
                              workExperience: updatedExperiences,
                            }));
                          }}
                          className="mt-2 block w-full rounded-md border border-gray-300 px-3 py-2"
                          placeholder="Enter Responsibilities"
                        />
                      </div>
                      <div>
                        <InputField
                          label="Skills"
                          type="text"
                          value={exp.skills || ''}
                          onChange={(e) => {
                            const updatedExperiences = [
                              ...(updatedStudent.workExperience || []),
                            ];
                            updatedExperiences[index] = {
                              ...updatedExperiences[index],
                              skills: e.target.value,
                            };
                            setUpdatedStudent((prev) => ({
                              ...prev,
                              workExperience: updatedExperiences,
                            }));
                          }}
                          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                          placeholder="Enter Skills"
                        />
                      </div>

                      {/* Remove Button - Only show for newly added experiences (those without an _id) */}
                      {!exp._id && (
                        <button
                          onClick={() => {
                            const updatedExperiences = [
                              ...(updatedStudent.workExperience || []),
                            ];
                            updatedExperiences.splice(index, 1);
                            setUpdatedStudent((prev) => ({
                              ...prev,
                              workExperience: updatedExperiences,
                            }));
                          }}
                          className="absolute right-2 top-2 rounded bg-red-500 px-2 py-1 text-xs text-white hover:bg-red-600"
                          type="button"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    onClick={() => {
                      const newExperience = {
                        companyName: '',
                        position: '',
                        startDate: null,
                        endDate: null,
                        responsibilities: '',
                        skills: '',
                      };
                      setUpdatedStudent((prev) => ({
                        ...prev,
                        workExperience: [
                          ...(prev.workExperience || []),
                          newExperience,
                        ],
                      }));
                    }}
                    className="mt-2 rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
                    type="button"
                  >
                    Add Work Experience
                  </button>
                </div>
                {/* <div className="mb-6">
                  <h4 className="text-md mb-4 font-semibold">
                    Work Experience
                  </h4>
                  {(updatedStudent.workExperience || []).map((exp, index) => (
                    <div
                      key={exp._id || index}
                      className="mb-4 grid grid-cols-3 gap-4 rounded border p-4"
                    >
                      <div>
                        <label className="block text-sm font-medium text-gray-700"></label>
                        <InputField
                          label="Company Name"
                          type="text"
                          value={exp.companyName || ''}
                          onChange={(e) => {
                            const updatedExperiences = [
                              ...(updatedStudent.workExperience || []),
                            ];
                            updatedExperiences[index] = {
                              ...updatedExperiences[index],
                              companyName: e.target.value,
                            };
                            setUpdatedStudent((prev) => ({
                              ...prev,
                              workExperience: updatedExperiences,
                            }));
                          }}
                          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                          placeholder="Enter Company Name"
                        />
                      </div>
                      <div>
                        <InputField
                          label="Position"
                          type="text"
                          value={exp.position || ''}
                          onChange={(e) => {
                            const updatedExperiences = [
                              ...(updatedStudent.workExperience || []),
                            ];
                            updatedExperiences[index] = {
                              ...updatedExperiences[index],
                              position: e.target.value,
                            };
                            setUpdatedStudent((prev) => ({
                              ...prev,
                              workExperience: updatedExperiences,
                            }));
                          }}
                          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                          placeholder="Enter Position"
                        />
                      </div>
                      <div>
                        <InputField
                          label="Start Date"
                          type="date"
                          value={
                            exp.startDate
                              ? new Date(exp.startDate)
                                  .toISOString()
                                  .split('T')[0]
                              : ''
                          }
                          onChange={(e) => {
                            const updatedExperiences = [
                              ...(updatedStudent.workExperience || []),
                            ];
                            updatedExperiences[index] = {
                              ...updatedExperiences[index],
                              startDate: new Date(e.target.value).toISOString(),
                            };
                            setUpdatedStudent((prev) => ({
                              ...prev,
                              workExperience: updatedExperiences,
                            }));
                          }}
                          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                        />
                      </div>
                      <div>
                        <InputField
                          label="End Date"
                          type="date"
                          value={
                            exp.endDate
                              ? new Date(exp.endDate)
                                  .toISOString()
                                  .split('T')[0]
                              : ''
                          }
                          onChange={(e) => {
                            const updatedExperiences = [
                              ...(updatedStudent.workExperience || []),
                            ];
                            updatedExperiences[index] = {
                              ...updatedExperiences[index],
                              endDate: new Date(e.target.value).toISOString(),
                            };
                            setUpdatedStudent((prev) => ({
                              ...prev,
                              workExperience: updatedExperiences,
                            }));
                          }}
                          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                        />
                      </div>
                      <div>
                        <TextField
                          label="Responsibilities"
                          value={exp.responsibilities || ''}
                          onChange={(e) => {
                            const updatedExperiences = [
                              ...(updatedStudent.workExperience || []),
                            ];
                            updatedExperiences[index] = {
                              ...updatedExperiences[index],
                              responsibilities: e.target.value,
                            };
                            setUpdatedStudent((prev) => ({
                              ...prev,
                              workExperience: updatedExperiences,
                            }));
                          }}
                          className="mt-2 block w-full rounded-md border border-gray-300 px-3 py-2"
                          placeholder="Enter Responsibilities"
                        />
                      </div>
                      <div>
                        <InputField
                          label="Skills"
                          type="text"
                          value={exp.skills || ''}
                          onChange={(e) => {
                            const updatedExperiences = [
                              ...(updatedStudent.workExperience || []),
                            ];
                            updatedExperiences[index] = {
                              ...updatedExperiences[index],
                              skills: e.target.value,
                            };
                            setUpdatedStudent((prev) => ({
                              ...prev,
                              workExperience: updatedExperiences,
                            }));
                          }}
                          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                          placeholder="Enter Skills"
                        />
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={() => {
                      const newExperience = {
                        companyName: '',
                        position: '',
                        startDate: null,
                        endDate: null,
                        responsibilities: '',
                        skills: '',
                      };
                      setUpdatedStudent((prev) => ({
                        ...prev,
                        workExperience: [
                          ...(prev.workExperience || []),
                          newExperience,
                        ],
                      }));
                    }}
                    className="mt-2 rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
                  >
                    Add Work Experience
                  </button>
                </div> */}

                <div className="relative">
                  <InputField
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={password}
                    onChange={handleInputChange}
                    className="w-full rounded border border-gray-300 px-3 py-2 dark:bg-navy-900"
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    className="absolute right-4 top-2/3 -translate-y-1/2 transform text-lg text-navy-700 dark:text-white"
                    onClick={togglePasswordVisibility}
                  >
                    {showPassword ? <RiEyeLine /> : <RiEyeCloseLine />}
                  </button>
                </div>

                {/* Enrollment Section */}
                {currentEnrollment && (
                  <div className="mb-6 rounded-lg border border-gray-200 p-4 dark:border-white/10">
                    <h4 className="text-md mb-4 font-semibold">Enrollment</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="mb-1 block text-sm font-medium text-navy-700 dark:text-white">
                          Course
                        </label>
                        <p className="rounded border bg-gray-50 px-3 py-2 text-sm dark:bg-navy-800 dark:text-white">
                          {currentEnrollment.course?.courseName || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-navy-700 dark:text-white">
                          Batch
                        </label>
                        <p className="rounded border bg-gray-50 px-3 py-2 text-sm dark:bg-navy-800 dark:text-white">
                          {currentEnrollment.batch?.batchNumber
                            ? `Batch ${currentEnrollment.batch.batchNumber}`
                            : 'N/A'}
                        </p>
                      </div>

                      {/* ---------- Correct wrongly assigned course / batch ---------- */}
                      <div className="md:col-span-2">
                        {!showEnrollmentEditor ? (
                          <button
                            type="button"
                            onClick={openEnrollmentEditor}
                            className="linear cursor-pointer rounded-xl border border-brand-500 px-4 py-2 text-sm font-medium text-brand-600 transition duration-200 hover:bg-brand-50 dark:text-brand-400 dark:hover:bg-navy-700"
                          >
                            Change Course / Batch
                          </button>
                        ) : (
                          <div className="rounded-lg border border-dashed border-brand-500 p-4">
                            <p className="mb-3 text-xs text-gray-600 dark:text-gray-300">
                              Use this only to fix a wrong entry made during
                              admission. It replaces the current course and batch
                              and does <b>not</b> change fees or swap history. For
                              a genuine course change, use <b>Batch Swap</b>{' '}
                              instead.
                            </p>

                            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                              <div>
                                <label className="mb-1 block text-sm font-medium text-navy-700 dark:text-white">
                                  Course
                                </label>
                                <select
                                  value={correction.courseId}
                                  disabled={loadingCourses}
                                  onChange={(e) =>
                                    handleCorrectionCourseChange(e.target.value)
                                  }
                                  className="w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
                                >
                                  <option value="">
                                    {loadingCourses
                                      ? 'Loading courses...'
                                      : 'Select Course'}
                                  </option>
                                  {courses.map((course) => (
                                    <option key={course._id} value={course._id}>
                                      {course.courseName}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              <div>
                                <label className="mb-1 block text-sm font-medium text-navy-700 dark:text-white">
                                  Batch Structure
                                </label>
                                <select
                                  value={correction.batchStructureId}
                                  disabled={
                                    !correction.courseId || loadingStructures
                                  }
                                  onChange={(e) =>
                                    handleCorrectionStructureChange(
                                      e.target.value
                                    )
                                  }
                                  className="w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal outline-none disabled:opacity-60 dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
                                >
                                  <option value="">
                                    {loadingStructures
                                      ? 'Loading structures...'
                                      : 'Select Batch Structure'}
                                  </option>
                                  {batchStructures.map((structure) => (
                                    <option
                                      key={structure._id}
                                      value={structure._id}
                                    >
                                      {structure.name}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              <div>
                                <label className="mb-1 block text-sm font-medium text-navy-700 dark:text-white">
                                  Batch
                                </label>
                                <select
                                  value={correction.batchId}
                                  disabled={
                                    !correction.batchStructureId || loadingBatches
                                  }
                                  onChange={(e) =>
                                    setCorrection((prev) => ({
                                      ...prev,
                                      batchId: e.target.value,
                                    }))
                                  }
                                  className="w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal outline-none disabled:opacity-60 dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
                                >
                                  <option value="">
                                    {loadingBatches
                                      ? 'Loading batches...'
                                      : 'Select Batch'}
                                  </option>
                                  {availableBatches.map((batch) => (
                                    <option key={batch._id} value={batch._id}>
                                      Batch {batch.batchNumber}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              <div className="md:col-span-3">
                                <InputField
                                  label="Reason (optional)"
                                  type="text"
                                  value={correction.reason}
                                  onChange={(e) =>
                                    setCorrection((prev) => ({
                                      ...prev,
                                      reason: e.target.value,
                                    }))
                                  }
                                  className="w-full rounded border border-gray-300 px-3 py-2 dark:bg-navy-900"
                                />
                              </div>
                            </div>

                            <div className="mt-4 flex gap-3">
                              <button
                                type="button"
                                disabled={
                                  correctionSaving ||
                                  !correction.courseId ||
                                  !correction.batchStructureId ||
                                  !correction.batchId ||
                                  !correctionChanged
                                }
                                onClick={() =>
                                  setShowCorrectionConfirmation(true)
                                }
                                className="linear cursor-pointer rounded-xl bg-brand-900 px-6 py-2 text-sm font-medium text-white transition duration-200 hover:bg-brand-800 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-brand-400 dark:hover:bg-brand-300"
                              >
                                {correctionSaving
                                  ? 'Saving...'
                                  : 'Save Correction'}
                              </button>
                              <button
                                type="button"
                                onClick={closeEnrollmentEditor}
                                className="linear cursor-pointer rounded-xl border border-gray-300 px-6 py-2 text-sm font-medium text-navy-700 transition duration-200 hover:bg-gray-100 dark:border-white/10 dark:text-white dark:hover:bg-navy-700"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">
                          Enrollment Date
                        </label>
                        <input
                          type="date"
                          value={
                            updatedStudent.enrollments?.[currentEnrollmentIndex]
                              ?.enrollmentDate
                              ? new Date(
                                  updatedStudent.enrollments[
                                    currentEnrollmentIndex
                                  ].enrollmentDate
                                )
                                  .toISOString()
                                  .split('T')[0]
                              : ''
                          }
                          onChange={(e) =>
                            handleCurrentEnrollmentFieldChange(
                              'enrollmentDate',
                              e.target.value
                            )
                          }
                          className="mt-2 w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">
                          Status
                        </label>
                        <select
                          value={
                            updatedStudent.enrollments?.[currentEnrollmentIndex]
                              ?.status || 'Active'
                          }
                          onChange={(e) =>
                            handleCurrentEnrollmentFieldChange(
                              'status',
                              e.target.value
                            )
                          }
                          className="mt-2 w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
                        >
                          <option value="Active">Active</option>
                          <option value="Dropped">Dropped</option>
                          <option value="Completed">Completed</option>
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <InputField
                          label="Notes"
                          type="text"
                          value={
                            updatedStudent.enrollments?.[currentEnrollmentIndex]
                              ?.notes || ''
                          }
                          onChange={(e) =>
                            handleCurrentEnrollmentFieldChange(
                              'notes',
                              e.target.value
                            )
                          }
                          className="w-full rounded border border-gray-300 px-3 py-2 dark:bg-navy-900"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Fees Section */}
                <div className="mb-6 rounded-lg border border-gray-200 p-4 dark:border-white/10">
                  <h4 className="text-md mb-4 font-semibold">Fees</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <InputField
                      label="Total Amount"
                      type="text"
                      value={feesData.totalAmount}
                      onChange={(e) => {
                        if (/^\d*$/.test(e.target.value)) {
                          handleFeeFieldChange('totalAmount', e.target.value);
                        }
                      }}
                      className="w-full rounded border border-gray-300 px-3 py-2 dark:bg-navy-900"
                    />
                    <div>
                      <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">
                        Payment Type
                      </label>
                      <select
                        value={feesData.isFullPayment ? 'true' : 'false'}
                        onChange={(e) =>
                          handleFeeFieldChange('isFullPayment', e.target.value === 'true')
                        }
                        className="mt-2 w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
                      >
                        <option value="true">Full Payment</option>
                        <option value="false">Installments</option>
                      </select>
                    </div>
                  </div>

                  {!feesData.isFullPayment && (
                    <div className="mt-4">
                      {feesData.installments.map((inst, idx) => (
                        <div key={idx} className="mb-4 rounded-lg border border-gray-200 p-4 dark:border-white/10">
                          <h6 className="mb-2 text-sm font-semibold">Installment {idx + 1}</h6>
                          <div className="grid grid-cols-3 gap-4">
                            <InputField
                              label="Amount"
                              value={inst.amount}
                              onChange={(e) =>
                                /^\d*$/.test(e.target.value) &&
                                handleInstallmentChange(idx, 'amount', e.target.value)
                              }
                              className="w-full rounded border border-gray-300 px-3 py-2 dark:bg-navy-900"
                            />
                            <div>
                              <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">
                                Due Date
                              </label>
                              <input
                                type="date"
                                value={inst.dueDate || ''}
                                onChange={(e) =>
                                  handleInstallmentChange(idx, 'dueDate', e.target.value)
                                }
                                className="mt-2 w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
                              />
                            </div>
                            <div className="flex items-end pb-3">
                              <label className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={inst.isEmiInstallment || false}
                                  onChange={(e) =>
                                    handleInstallmentChange(idx, 'isEmiInstallment', e.target.checked)
                                  }
                                  className="form-checkbox"
                                />
                                <span className="text-sm">EMI</span>
                              </label>
                            </div>
                          </div>
                          {inst.isEmiInstallment && (
                            <div className="mt-3 md:w-1/3">
                              <InputField
                                label="After Loan Deduction"
                                value={inst.afterLoanDeduction || ''}
                                onChange={(e) =>
                                  /^\d*$/.test(e.target.value) &&
                                  handleInstallmentChange(idx, 'afterLoanDeduction', e.target.value)
                                }
                                className="w-full rounded border border-gray-300 px-3 py-2 dark:bg-navy-900"
                              />
                            </div>
                          )}
                          <div className="mt-2 text-right">
                            <button
                              onClick={() => removeInstallment(idx)}
                              className="text-sm text-red-600 hover:text-red-800"
                              type="button"
                            >
                              Remove Installment
                            </button>
                          </div>
                        </div>
                      ))}
                      <button
                        onClick={addInstallment}
                        className="mt-2 rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
                        type="button"
                      >
                        Add Installment
                      </button>
                    </div>
                  )}
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    onClick={handleSaveButtonClick}
                    className="rounded bg-green-500 px-4 py-2 text-white hover:bg-green-600"
                  >
                    Save Changes
                  </button>
                  <button
                    onClick={() => setIsEditing(false)}
                    className="ml-3 rounded bg-gray-500 px-4 py-2 text-white hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div className="mb-6 rounded-lg border border-dashed border-brand-500 bg-white p-6 shadow-md dark:bg-navy-900">
                  <h3 className="mb-4 text-lg font-semibold text-brand-600 dark:text-white">
                    Personal Details
                  </h3>
                  <div className="grid grid-cols-1 gap-4">
                    {/* Date of Birth */}
                    <div className="border-b pb-2">
                      <p className="font-semibold text-navy-700 dark:text-white">
                        Date of Birth:
                      </p>
                      <p className="text-gray-700 dark:text-gray-300">
                        {new Date(
                          selectedStudent.dateOfBirth
                        ).toLocaleDateString()}
                      </p>
                    </div>

                    {/* Gender */}
                    <div className="border-b pb-2">
                      <p className="font-semibold text-navy-700 dark:text-white">
                        Gender:
                      </p>
                      <p className="text-gray-700 dark:text-gray-300">
                        {selectedStudent.gender}
                      </p>
                    </div>

                    {/* Parent Name */}
                    <div className="border-b pb-2">
                      <p className="font-semibold text-navy-700 dark:text-white">
                        Parent Name:
                      </p>
                      <p className="text-gray-700 dark:text-gray-300">
                        {selectedStudent.parentName}
                      </p>
                    </div>

                    {/* Parent Contact */}
                    <div className="border-b pb-2">
                      <p className="font-semibold text-navy-700 dark:text-white">
                        Parent Contact Number:
                      </p>
                      <p className="text-gray-700 dark:text-gray-300">
                        {selectedStudent.ParentContactNo}
                      </p>
                    </div>

                    {/* Address */}
                    <div className="border-b pb-2">
                      <p className="font-semibold text-navy-700 dark:text-white">
                        Address:
                      </p>
                      <p className="leading-relaxed text-gray-700 dark:text-gray-300">
                        {selectedStudent.address.street},{' '}
                        {selectedStudent.address.city}, <br />
                        {selectedStudent.address.state} -{' '}
                        {selectedStudent.address.postalCode},{' '}
                        {selectedStudent.address.country}
                      </p>
                    </div>

                    {/* Ready to Place */}
                    <div className="border-b pb-2">
                      <p className="font-semibold text-navy-700 dark:text-white">
                        Ready to Place:
                      </p>
                      <p className="text-gray-700 dark:text-gray-300">
                        {selectedStudent.readyToPlace ? 'Yes' : 'No'}
                      </p>
                    </div>

                    {/* Placement Priority */}
                    <div className="border-b pb-2">
                      <p className="font-semibold text-navy-700 dark:text-white">
                        Placement Priority:
                      </p>
                      <p className="text-gray-700 dark:text-gray-300">
                        {selectedStudent.placementPriority}
                      </p>
                    </div>

                    {/* Job Location Preference */}
                    <div className="border-b pb-2">
                      <p className="font-semibold text-navy-700 dark:text-white">
                        Job Location Preference:
                      </p>
                      <p className="text-gray-700 dark:text-gray-300">
                        {selectedStudent.jobLocationPreference.join(', ')}
                      </p>
                    </div>

                    {/* Privacy Policy */}
                    <div>
                      <p className="font-semibold text-navy-700 dark:text-white">
                        Privacy Policy Accepted:
                      </p>
                      <p className="text-gray-700 dark:text-gray-300">
                        {selectedStudent.privacyPolicyAccepted ? 'Yes' : 'No'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Staff Notes (internal, not visible to student) */}
                <div className="mb-6 rounded-lg border border-dashed border-brand-500 bg-white p-6 shadow-md dark:bg-navy-900">
                  <h3 className="mb-1 text-lg font-semibold text-brand-600 dark:text-white">
                    Staff Notes
                  </h3>
                  <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
                    Internal – not visible to student
                  </p>
                  <p className="whitespace-pre-wrap text-gray-700 dark:text-gray-300">
                    {selectedStudent.staffNotes || '—'}
                  </p>
                </div>

                <div className="mb-6 rounded-lg border border-dashed border-brand-500 bg-white p-6 shadow-md dark:bg-navy-900">
                  <h3 className="mb-4 text-lg font-semibold text-brand-600 dark:text-white">
                    Records
                  </h3>
                  <div className="grid grid-cols-1 gap-4">
                    {/* Academic Records */}
                    <div className="rounded-lg border bg-gray-50 p-4 shadow-sm dark:bg-navy-800">
                      <p className="font-semibold text-navy-700 dark:text-white">
                        Academic Records:
                      </p>
                      {selectedStudent.academicRecords.length > 0 ? (
                        <ul className="mt-2 list-inside list-disc space-y-2 text-gray-700 dark:text-gray-300">
                          {selectedStudent.academicRecords.map((record) => (
                            <li key={record._id} className="leading-relaxed">
                              <span className="font-medium text-brand-600 dark:text-brand-400">
                                {record.level} - {record.institutionName} (
                                {record.yearOfCompletion})
                              </span>
                              : {record.percentage}% - GPA: {record.gpa}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="mt-2 text-gray-500 dark:text-gray-400">
                          No academic records available.
                        </p>
                      )}
                    </div>

                    {/* Work Experience */}
                    <div className="rounded-lg border bg-gray-50 p-4 shadow-sm dark:bg-navy-800">
                      <p className="font-semibold text-navy-700 dark:text-white">
                        Work Experience:
                      </p>
                      {selectedStudent.workExperience.length > 0 ? (
                        <ul className="mt-2 list-inside list-disc space-y-2 text-gray-700 dark:text-gray-300">
                          {selectedStudent.workExperience.map((experience) => (
                            <li
                              key={experience._id}
                              className="leading-relaxed"
                            >
                              <span className="font-medium text-brand-600 dark:text-brand-400">
                                {experience.position} at{' '}
                                {experience.companyName}
                              </span>{' '}
                              from{' '}
                              {new Date(
                                experience.startDate
                              ).toLocaleDateString()}{' '}
                              to{' '}
                              {new Date(
                                experience.endDate
                              ).toLocaleDateString()}
                              .
                              <br />
                              <span className="text-gray-700 dark:text-gray-400">
                                Responsibilities: {experience.responsibilities}.
                              </span>{' '}
                              <span className="text-gray-700 dark:text-gray-400">
                                Skills: {experience.skills}.
                              </span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="mt-2 text-gray-700 dark:text-gray-400">
                          No work experience available.
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <CourseEnrollmentDisplay enrollments={enrollments} />

                {showFirstConfirmation && (
                  <div className="bg-black fixed inset-0 flex items-center justify-center bg-opacity-50">
                    <div className="mx-4 w-full max-w-md rounded-lg bg-white p-4 shadow-lg dark:bg-navy-800 md:mx-0 md:p-8">
                      <div className="flex items-start">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-12 w-12 rounded-full border border-blue-100 bg-blue-50 p-2 text-blue-400 md:h-16 md:w-16 md:p-3"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <div className="ml-3 flex-1 md:ml-4">
                          <h2 className="text-base font-medium dark:text-white md:text-lg">
                            Deactivate Student Account?
                          </h2>
                          <p className="mt-2 text-sm text-gray-700">
                            Are you sure you want to deactivate this student's
                            account?
                          </p>
                        </div>
                      </div>
                      <div className="mt-6 flex justify-end">
                        <button
                          onClick={() => {
                            handleDeactivateStudent();
                          }}
                          className="ml-3 rounded-full border-2 border-red-500 bg-red-500 px-4 py-2 text-sm font-medium text-white shadow-sm transition duration-200 hover:bg-red-600 hover:shadow-lg"
                        >
                          Deactivate
                        </button>
                        <button
                          onClick={() => setShowFirstConfirmation(false)}
                          className="ml-3 rounded-full border-2 border-gray-300 bg-white px-4 py-2 text-sm font-medium text-navy-700 shadow-sm transition duration-200 hover:bg-gray-100 hover:shadow-lg"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <FeeDetails studentId={selectedStudent._id} />

      <div className=" mx-auto  mt-6 w-full max-w-6xl text-navy-700 dark:text-white">
        <div className="relative">
          <div
            className="absolute inset-0 flex items-center"
            aria-hidden="true"
          >
            <div className="w-full border-t border-gray-300 dark:border-navy-600"></div>
          </div>
          <div className="relative mb-7 flex justify-center">
            <h2 className="bg-white px-3 text-xl font-bold tracking-tight text-gray-900 dark:bg-navy-800 dark:text-white">
              OverAll Performance
            </h2>
          </div>
        </div>

        {selectedStandard && (
          <StudentOverviewTable
            data={selectedStandard}
            mcqData={mcqData}
            qaData={qaData}
            mcqAssignmentData={mcqAssignmentData}
            qaAssignmentData={qaAssignmentData}
            mockData={mockData}
            finalMockData={finalMockData}
            isLoading={loadingStudentData}
          />
        )}
      </div>
      <ConfirmationModal
        isOpen={showCorrectionConfirmation}
        onClose={() => setShowCorrectionConfirmation(false)}
        onConfirm={handleCorrectEnrollment}
        title="Correct Course / Batch"
        message={`This will move ${updatedStudent.name} from ${
          currentEnrollment?.course?.courseName || 'N/A'
        } (Batch ${
          currentEnrollment?.batch?.batchNumber || 'N/A'
        }) to ${selectedCorrectionCourseName || 'the selected course'} (Batch ${
          selectedCorrectionBatchNumber || 'N/A'
        }). Fees and swap history are not changed.`}
        confirmText="Yes, Correct It"
        cancelText="Cancel"
        icon="warning"
      />
      <ConfirmationModal
        isOpen={showEditConfirmation}
        onClose={() => setShowEditConfirmation(false)}
        onConfirm={handleEditStudent}
        title=" Save Student Changes"
        message={`  Are you sure you want to update ${updatedStudent.name}'s information?`}
        confirmText="Yes, Save Changes"
        cancelText="Cancel"
        icon="info"
      />
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        file={selectedFile}
        fetchData={fetchData}
      />
      <div className="mt-[20px] flex justify-center px-4 px-[27px] py-[16px]">
        <div className="flex-end absolute bottom-4  right-12 flex justify-end px-3 pt-2  md:flex-row md:px-8">
          <button
            onClick={handleBackClick}
            className="linear cursor-pointer rounded-xl bg-brand-900 px-16 py-2 font-medium text-white transition duration-200 hover:bg-brand-800 active:bg-brand-700 dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:opacity-90 md:ml-2"
          >
            Back
          </button>
        </div>
      </div>
    </div>
  );
};

export default Index;
